import React, { Fragment } from 'react';
import { concat, filter, isNull, find, isNil, isNaN } from 'lodash';
import { AiFillCaretRight } from 'react-icons/ai';
import { DATE_FORMAT } from '../../constants/service/common/utilConstants';
import {
  DISCOUNT_TYPE,
  GENERAL_DELIVERY_REQUIRED_DATE,
  PRODUCT_DELIVERY_TYPE,
  PRODUCT_MAXIMUM_DISCOUNT_RATE,
  PRODUCT_MAXIMUM_PRICE,
  PRODUCT_SEARCH_PRICE_CHECK_BOX_LIST,
  PRODUCT_SEARCH_RATE_CHECK_BOX_LIST,
  MINIMUM_PRICE_2000_WON_CATEGORY_NAME_LIST,
  ORDER_MADE_DELIVERY_COMPLETE_ADDITIONAL_DATE,
  INVALID_BULK_EDIT_PRODUCT_CASE_TYPE_TEXT,
  PAY_METHOD_NAME_TO_PAYMENT_METHOD_TYPE,
  PRODUCT_PAY_METHOD_VALUE_TYPE,
  PRODUCT_DETAIL_PAYMENT_METHOD_TYPE_KEY_LIST,
} from '../../constants/service/app/productConstants';
import { PAY_METHOD_TYPE, PAYMENT_METHOD_TYPE_TO_KEY } from '../../constants/service/app/orderConstants';
import { setDash, setNumberWithComma } from '../common';
import moment, { setDateFormat } from '../../lib/moment';

/**
 *  상품 배송 방법,
 *  주문 시 제작 제품 여부,
 *  주문 시 제작 소요시간,
 *  바로 배송 기준 시간 설정하기
 */
export const setProductDeliveryTypeInfo = (data) => {
  const deliveryType = data.deliveryType;
  const isOrderMade = data.deliveryType === PRODUCT_DELIVERY_TYPE.GENERAL_DELIVERY ? data.isOrderMade : null;
  const orderMadeDate = !isNull(isOrderMade) && isOrderMade ? data.orderMadeDate : null;
  const directDeliveryTimeLimit =
    data.deliveryType === PRODUCT_DELIVERY_TYPE.DIRECT_DELIVERY ? data.directDeliveryTimeLimit : null;

  return {
    deliveryType,
    isOrderMade,
    orderMadeDate,
    directDeliveryTimeLimit,
  };
};

/**
 *  바로 배송 시간 구하기
 */
export const getDirectDeliveryTimeAvailable = (targetTime, format) => {
  const directDeliveryTime = setDateFormat(moment().set({ hour: targetTime, minute: 0 }), format);
  const currentTime = setDateFormat(moment(), format);

  return !moment(currentTime).isAfter(directDeliveryTime);
};

/**
 * 상품 배송 방법에 따라서
 * 상품 상세에 보여질 문구 설정하기
 */
export const setDeliveryTypeInfo = (data) => {
  const { deliveryType, isOrderMade, orderMadeDate, directDeliveryTimeLimit, showActualValue } = data;

  /**
   * 일반 배송인 경우
   */
  if (deliveryType === PRODUCT_DELIVERY_TYPE.GENERAL_DELIVERY && !isOrderMade) {
    return <Fragment>{setGeneralDeliveryTypeInfoWord(GENERAL_DELIVERY_REQUIRED_DATE)}</Fragment>;
  } else if (deliveryType === PRODUCT_DELIVERY_TYPE.GENERAL_DELIVERY && isOrderMade && !isNull(orderMadeDate)) {
    /**
     * 주문 시 제작인 경우
     * 발송 처리부일로부터 배송 완료 일시까지 소요되는 시간을 추가로 더해줌
     */
    return (
      <Fragment>
        <div>{setGeneralDeliveryTypeInfoWord(orderMadeDate + ORDER_MADE_DELIVERY_COMPLETE_ADDITIONAL_DATE)}</div>
        <div>
          <span>(상품 제작에 {orderMadeDate}일, 배송에 2일이 소요됩니다.)</span>
        </div>
      </Fragment>
    );
  } else if (deliveryType === PRODUCT_DELIVERY_TYPE.DIRECT_DELIVERY && !isNull(directDeliveryTimeLimit)) {
    /**
     * 바로 배송인 경우
     */
    const isAfterDirectDeliveryTime = getDirectDeliveryTimeAvailable(
      directDeliveryTimeLimit,
      DATE_FORMAT.YMD_HM_DASH_FORMAT,
    );

    /**
     *  현재 시간이 기준 시간의 이후인 경우
     */
    if (showActualValue && !isAfterDirectDeliveryTime) {
      return (
        <Fragment>
          <span className="emphasize">평균 2일 내 배송/</span>
          <span> 주말, 공휴일 제외</span>
        </Fragment>
      );
    } else {
      /**
       *  현재 시간이 기준 시간의 이전인 경우
       */
      const amPm = setDateFormat(moment().set({ hour: directDeliveryTimeLimit }), DATE_FORMAT.AM_PM_FORMAT);

      return (
        <Fragment>
          <span className="emphasize">{amPm}시까지 주문시, </span>
          <span>오늘 바로 배송 시작!</span>
        </Fragment>
      );
    }
  } else return null;
};

/**
 * 일반 배송의 주문 제작일에 따른 문구 설정
 */
const setGeneralDeliveryTypeInfoWord = (date) => {
  return (
    <Fragment>
      <span className="emphasize">평균 {date}일 내 배송 / </span>
      <span>주말, 공휴일 제외</span>
    </Fragment>
  );
};

/**
 *  할인 타입에 따라 할인액, 할인율, 실판매가 구하기
 */
export const setPriceDataFromDiscountType = (discountType, originPrice, amount = 10, rate = 10) => {
  let discountAmount,
    discountRate,
    salesPrice = null;

  /**
   *  정액 할인
   */
  if (discountType === DISCOUNT_TYPE.FIXED_AMOUNT_DISCOUNT || isNull(discountType)) {
    salesPrice = originPrice - amount;
    discountAmount = amount;
    discountRate = (amount / originPrice) * 100 < 1 ? 1 : originPrice ? Math.floor((amount / originPrice) * 100) : '-';
    discountType = DISCOUNT_TYPE.FIXED_AMOUNT_DISCOUNT;
  } else if (discountType === DISCOUNT_TYPE.FIXED_RATE_DISCOUNT) {
    /**
     *  정률 할인
     */
    salesPrice = Math.floor((originPrice * (100 - rate) * 0.01) / 10) * 10;
    discountAmount = originPrice - salesPrice;
    discountRate = rate;
    discountType = DISCOUNT_TYPE.FIXED_RATE_DISCOUNT;
  }

  const setValueWithoutNaN = (value, defaultValue = '-') =>
    !isNaN(value) && typeof value === 'number' ? value : defaultValue;

  return {
    discountAmount: setValueWithoutNaN(discountAmount),
    discountRate: setValueWithoutNaN(discountRate),
    salesPrice: setValueWithoutNaN(salesPrice, 0),
    discountType,
  };
};

/**
 *  상품 가격 범위 시작 값 설정(직접 입력)
 */
export const setStartProductValueRange = (rangeStart, rangeEnd) => {
  /**
   *  시작 값은 끝 값보다 클 수 없다.
   */
  if (rangeStart > rangeEnd) return rangeEnd;

  /**
   *  null 인 경우 0으로 취급한다
   */
  if (!rangeStart) rangeStart = 0;

  return rangeStart;
};

/**
 *  상품 가격 범위 끝 값 설정(직접 입력)
 */
export const setEndProductValueRange = (rangeStart, rangeEnd, maxValue) => {
  /**
   *  시작 값은 끝 값보다 클 수 없다.
   */
  if (rangeStart > rangeEnd) return rangeStart;

  /**
   *  null 인 경우 최대값으로 취급한다
   */
  if (!rangeEnd) rangeEnd = maxValue;
  return rangeEnd;
};

/**
 *  상품 가격 범위 설정 (체크 박스)
 */
export const setPriceRangeOnCheckBox = (checked, checkItem, priceData) => {
  let checkList;
  let startPrice;
  let endPrice;

  /**
   *  체크를 한 경우
   */
  if (checked) {
    /**
     *  직접 입력 체크를 한 경우 이전 체크 리스트, 가격 범위을 삭제한다.
     */
    if (checkItem.id === PRODUCT_SEARCH_PRICE_CHECK_BOX_LIST.length) {
      checkList = [checkItem.id];
      startPrice = [0];
      endPrice = [PRODUCT_MAXIMUM_PRICE];
    } else {
      /**
       *  범위를 체크한 경우 체크한 값에 따라 가격의 범위를 설정한다.
       */
      /**
       *  이전에 직접입력을 선택했을 경우 직접 입력 체크를 해제한다.
       */
      if (priceData.checkList.indexOf(PRODUCT_SEARCH_PRICE_CHECK_BOX_LIST.length) !== -1) {
        checkList = [checkItem.id];
        startPrice = [checkItem.value.startPrice];
        endPrice = [checkItem.value.endPrice];
      } else {
        /**
         *  나머지 경우 체크 리스트와 검색 범위를 설정한다.
         */
        /**
         *  체크값에 따라 체크리스트를 갱신한다.
         */
        checkList = concat(priceData.checkList, checkItem.id);
        /**
         *  체크 리스트 값에 따라 범위를 설정한다.
         */
        startPrice = concat(priceData.startPrice, checkItem.value.startPrice);
        endPrice = concat(priceData.endPrice, checkItem.value.endPrice);
      }
    }
  } else {
    /**
     *  체크를 해제한 경우
     */
    /**
     *  직접 입력 체크 해제 시 체크리스트, 가격 범위를 초기화한다
     */
    if (checkItem.id === PRODUCT_SEARCH_PRICE_CHECK_BOX_LIST.length) {
      checkList = [];
      startPrice = [];
      endPrice = [];
    } else {
      /**
       *  범위를 체크해제한 체크한 값에 따라 가격의 범위를 설정한다.
       */
      /**
       *  체크값에 따라 체크리스트를 갱신한다.
       */
      checkList = filter(priceData.checkList, (id) => id !== checkItem.id);
      /**
       *  체크 리스트 값에 따라 범위를 설정한다.
       */
      startPrice = filter(priceData.startPrice, (price) => price !== checkItem.value.startPrice);
      endPrice = filter(priceData.endPrice, (price) => price !== checkItem.value.endPrice);
    }
  }

  return {
    checkList,
    startPrice,
    endPrice,
  };
};

/**
 *  상품 할인율 범위 설정 (체크 박스)
 */
export const setRateRangeOnCheckBox = (checked, checkItem, rateData) => {
  let checkList;
  let startRate;
  let endRate;

  /**
   *  체크를 한 경우
   */
  if (checked) {
    /**
     *  직접 입력 체크를 한 경우 이전 체크 리스트, 가격 범위을 삭제한다.
     */
    if (checkItem.id === PRODUCT_SEARCH_RATE_CHECK_BOX_LIST.length) {
      checkList = [checkItem.id];
      startRate = [0];
      endRate = [PRODUCT_MAXIMUM_DISCOUNT_RATE];
    } else {
      /**
       *  범위를 체크한 경우 체크한 값에 따라 가격의 범위를 설정한다.
       */
      /**
       *  이전에 직접입력을 선택했을 경우 직접 입력 체크를 해제한다.
       */
      if (rateData.checkList.indexOf(PRODUCT_SEARCH_RATE_CHECK_BOX_LIST.length) !== -1) {
        checkList = [checkItem.id];
        startRate = [checkItem.value.startRate];
        endRate = [checkItem.value.endRate];
      } else {
        /**
         *  나머지 경우 체크 리스트와 검색 범위를 설정한다.
         */
        /**
         *  체크값에 따라 체크리스트를 갱신한다.
         */
        checkList = concat(rateData.checkList, checkItem.id);
        /**
         *  체크 리스트 값에 따라 범위를 설정한다.
         */
        startRate = concat(rateData.startRate, checkItem.value.startRate);
        endRate = concat(rateData.endRate, checkItem.value.endRate);
      }
    }
  } else {
    /**
     *  체크를 해제한 경우
     */
    /**
     *  직접 입력 체크 해제 시 체크리스트, 가격 범위를 초기화한다
     */
    if (checkItem.id === PRODUCT_SEARCH_RATE_CHECK_BOX_LIST.length) {
      checkList = [];
      startRate = [];
      endRate = [];
    } else {
      /**
       *  범위를 체크해제한 체크한 값에 따라 가격의 범위를 설정한다.
       */
      /**
       *  체크값에 따라 체크리스트를 갱신한다.
       */
      checkList = filter(rateData.checkList, (id) => id !== checkItem.id);
      /**
       *  체크 리스트 값에 따라 범위를 설정한다.
       */
      startRate = filter(rateData.startRate, (rate) => rate !== checkItem.value.startRate);
      endRate = filter(rateData.endRate, (rate) => rate !== checkItem.value.endRate);
    }
  }

  return {
    checkList,
    startRate,
    endRate,
  };
};

/**
 *  판매가가 9,900원 이상만 올릴 수 있는 카테고리 id 리스트 만들기
 */
export const setMinimumPrice2000WonCategoryIdList = (categoryList) => {
  return MINIMUM_PRICE_2000_WON_CATEGORY_NAME_LIST.map((name) => {
    const categoryItem = find(categoryList, (category) => category.name === name);
    return categoryItem ? categoryItem.id : null;
  }).filter((item) => item);
};

/**
 *  엑셀 일괄 편집 모달에서 테이블의 데이터를 설정해주기
 */
export const setExcelBulkEditProduct = (data, priceUnit, dateFormat) => {
  if (!isNil(data) && !isNaN(data)) {
    if (data.name && data.hasOwnProperty('id')) {
      return setDash(data.name);
    } else if (data.invalidTypeList) {
      return data.invalidTypeList.map((type, index) => (
        <div key={index}>{INVALID_BULK_EDIT_PRODUCT_CASE_TYPE_TEXT[type]}</div>
      ));
    } else if (!isNil(priceUnit)) {
      return setNumberWithComma(data, priceUnit);
    } else if (!isNil(dateFormat)) {
      return setDateFormat(data, dateFormat);
    } else {
      return data;
    }
  } else return '-';
};

/**
 *  엑셀 일괄 편집 모달에서 invalid 존재 여부를 확인
 */
export const setIsExcelBulkEditProductInvalid = (data, isClassName = true) => {
  let result = isClassName ? '' : false;

  if (!isNil(data) && data.invalidTypeList) result = isClassName ? 'invalid' : true;
  return result;
};

/**
 *  상품 배송 방법에 따라서
 *  상품 상세 미리보기 모달에서 보여질 배송 방법 정보 안내 문구 설정하기
 */
export const setDeliveryTypeInfoContent = (data) => {
  const { isTodayDelivery, deliveryType, orderMadeDate, directDeliveryTimeLimit } = data;
  let title;
  let content;
  let subContent;

  /**
   *  오늘출발 상품 안내
   */
  if (isTodayDelivery) {
    title = '오늘출발 상품 안내';
    content = '아몬즈가 책임지고 당일 배송해드리는 상품입니다.';
    subContent = (
      <div className="delivery-info-table">
        <div className="head">
          <div className="th">주문일시</div>
          <div className="th">발송 예정일</div>
        </div>
        <div className="body">
          <div className="tr">
            <div className="td">평일 오후 3시 이전</div>
            <AiFillCaretRight />
            <div className="td">
              <strong>당일 발송</strong>
            </div>
          </div>
          <div className="tr">
            <div className="td">주말, 공휴일</div>
            <AiFillCaretRight />
            <div className="td">
              <strong>가장 빠른 영업일 발송</strong>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (deliveryType === PRODUCT_DELIVERY_TYPE.GENERAL_DELIVERY) {
    /**
     *  주문제작 상품 안내
     */
    title = '주문제작 상품 안내';
    content = (
      <Fragment>
        <div>주문 접수 후 디자이너가 정성들여 제작하는 상품입니다.</div>
        <div>제작이 시작된 이후에는, 고객님의 사유로 인한 취소가</div>
        <div>제한될 수 있습니다.</div>
      </Fragment>
    );
    subContent = (
      <div className="delivery-info-table">
        <div className="head">
          <div className="th">평균 배송 기간</div>
          <div className="th">제작 기간</div>
          <div className="th">운송 기간</div>
        </div>
        <div className="body">
          <div className="tr">
            <div className="td three-divide">{orderMadeDate + ORDER_MADE_DELIVERY_COMPLETE_ADDITIONAL_DATE}일</div>
            <div className="td three-divide">{orderMadeDate}일</div>
            <div className="td three-divide">{ORDER_MADE_DELIVERY_COMPLETE_ADDITIONAL_DATE}일</div>
          </div>
        </div>
      </div>
    );
  } else if (deliveryType === PRODUCT_DELIVERY_TYPE.DIRECT_DELIVERY) {
    /**
     *  바로배송 상품 안내
     */
    const amPm = setDateFormat(moment().set({ hour: directDeliveryTimeLimit }), DATE_FORMAT.AM_PM_FORMAT);
    title = '바로배송 상품 안내';
    content = '브랜드가 당일 배송해드리는 상품입니다.';
    subContent = (
      <div className="delivery-info-table">
        <div className="head">
          <div className="th">주문일시</div>
          <div className="th">발송 예정일</div>
        </div>
        <div className="body">
          <div className="tr">
            <div className="td">평일 {amPm}시 이전</div>
            <AiFillCaretRight />
            <div className="td">
              <strong>당일 발송</strong>
            </div>
          </div>
          <div className="tr">
            <div className="td">주말, 공휴일</div>
            <AiFillCaretRight />
            <div className="td">
              <strong>가장 빠른 영업일 발송</strong>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <div className="delivery-type-info-title">{title}</div>

      <div className="delivery-type-info-content">{content}</div>

      <div className="delivery-type-info-sub-content">{subContent}</div>
    </Fragment>
  );
};

/**
 *  상품 결제 타입에 해당되는 결제수단 정보 설정
 */
export const setProductPayMethodTypeInfo = (list) => {
  /**
   *  상품 결제 타입에 해당되는 결제수단 정보
   */
  const productPayMethodTypeInfo = list.reduce((acc, item) => {
    /**
     *  가능한 결제수단 리스트
     */
    acc[item.id] = {
      list: [],
    };
    PRODUCT_DETAIL_PAYMENT_METHOD_TYPE_KEY_LIST.forEach((key) => {
      if (item[key] !== PRODUCT_PAY_METHOD_VALUE_TYPE.UN_USED) {
        acc[item.id].list.push(PAY_METHOD_NAME_TO_PAYMENT_METHOD_TYPE[key]);
      }
    });

    /**
     *  카드 결제가 가능한 경우
     *  가능한 번위가 전체/일부 인지 확인하는 값
     */
    const cardIndex = item[PAYMENT_METHOD_TYPE_TO_KEY[PAY_METHOD_TYPE.CARD]];
    if (cardIndex !== PRODUCT_PAY_METHOD_VALUE_TYPE.UN_USED) {
      acc[item.id].card = cardIndex;
    }

    return acc;
  }, {});

  return productPayMethodTypeInfo;
};
